import { matchMetrics, validateState } from "./game";
import { dailyReleaseThrough, dailyCallMatched, isDailySessionRestorable, londonDateKey, scenarioForDate } from "./daily";
import type { DailyResult, GameSettings, GameState, LocalStats } from "./types";

export const SAVE_KEY = "trump-table:save:v2";
export const SETTINGS_KEY = "trump-table:settings:v2";
export const STATS_KEY = "trump-table:stats:v2";
export const SCORE_EVENT_KEY = "trump-table:last-score-event:v2";
export const VISITED_KEY = "trump-table:visited:v1";

export const DEFAULT_STATS: LocalStats = {
  games: 0,
  wins: 0,
  hands: 0,
  gameHands: 0,
  calls: 0,
  callsMade: 0,
  euchres: 0,
  aloneCalls: 0,
  aloneMade: 0,
  dailyDates: [],
  dailyResults: [],
  streak: 0,
};

export const DEFAULT_SETTINGS: GameSettings = {
  coach: true,
  sound: true,
  reducedMotion: false,
  confirmPlay: false,
  difficulty: "standard",
  locale: "en",
};

export function loadGame(): GameState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GameState;
    if (!validateState(parsed)) throw new Error("Invalid save");
    // Daily sessions restore only while their London identity (date key,
    // scenario id, seed) still matches the preloaded schedule and the hand is
    // inside its recovery window; legacy device-local sessions are dropped.
    if (parsed.mode === "daily" && !isDailySessionRestorable(parsed, londonDateKey())) {
      throw new Error("Daily save is not restorable under the London calendar");
    }
    return parsed;
  } catch {
    localStorage.removeItem(SAVE_KEY);
    return null;
  }
}

export function saveGame(state: GameState): void {
  localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  if (pendingDaily()?.gameId === state.gameId) localStorage.removeItem(PENDING_DAILY_KEY);
}

export function clearGame(): void {
  localStorage.removeItem(SAVE_KEY);
}

export function loadVisited(): boolean {
  try {
    return localStorage.getItem(VISITED_KEY) === "1";
  } catch {
    return false;
  }
}

export function markVisited(): void {
  try {
    localStorage.setItem(VISITED_KEY, "1");
  } catch {
    // Private-mode storage failures must never block entering the game.
  }
}

function systemPrefersReducedMotion(): boolean {
  try {
    return (
      typeof globalThis.matchMedia === "function"
      && globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

export function loadSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    // With no saved settings the reduced-motion default follows the OS
    // preference; once settings exist the user's explicit choice wins.
    if (raw === null) return { ...DEFAULT_SETTINGS, reducedMotion: systemPrefersReducedMotion() };
    const merged = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    // Drop the retired filter-based light theme key from older saves.
    delete (merged as { theme?: unknown }).theme;
    // Older saves predate the difficulty key; merge fills the default, and
    // anything unrecognized falls back to standard.
    if (!["relaxed", "standard", "sharp"].includes(merged.difficulty)) merged.difficulty = "standard";
    return merged;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(settings: GameSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function loadStats(): LocalStats {
  try {
    const stats = { ...DEFAULT_STATS, ...JSON.parse(localStorage.getItem(STATS_KEY) ?? "{}") };
    stats.dailyDates = Array.isArray(stats.dailyDates) ? stats.dailyDates.slice(-366) : [];
    stats.dailyResults = Array.isArray(stats.dailyResults)
      ? stats.dailyResults
        // Accepts both the legacy shape (no scenarioId/callMatched) and the
        // current one; anything malformed is dropped.
        .filter((result: DailyResult) =>
          result
          && typeof result.date === "string"
          && typeof result.won === "boolean"
          && Number.isFinite(result.margin)
          && Number.isInteger(result.hands)
          && result.hands > 0
          && typeof result.assisted === "boolean"
          && (result.scenarioId === undefined || typeof result.scenarioId === "string")
          && (result.callMatched === undefined || typeof result.callMatched === "boolean")
          && (result.calendar === undefined || result.calendar === "london"))
        .slice(-366)
      : [];
    // The streak belongs to the London calendar only: legacy device-local
    // entries stay readable but never manufacture a London streak.
    stats.streak = dailyStreak(londonDailyDates(stats.dailyResults));
    return stats;
  } catch {
    return DEFAULT_STATS;
  }
}

export function saveStats(stats: LocalStats): void {
  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
}

/** Completion dates recorded under the London calendar (v2), newest last. */
function londonDailyDates(results: DailyResult[]): string[] {
  return results.filter((result) => result.calendar === "london").map((result) => result.date);
}

export function recordHandStats(current: LocalStats, state: GameState): LocalStats {
  if (!state.lastHand) return current;
  const makerTeam = state.lastHand.makerTeam;
  const makerSucceeded = state.lastHand.tricks[makerTeam] >= 3;
  const completed = state.phase === "completed";
  const resultMetrics = matchMetrics(state);
  const dailyResults = [...current.dailyResults];
  // Only London-calendar sessions whose identity still matches the preloaded
  // schedule record a v2 result; legacy device-local sessions never do.
  if (
    completed
    && state.mode === "daily"
    && state.dailyDate
    && state.dailyCalendar === "london"
    && !dailyResults.some((result) => result.calendar === "london" && result.date === state.dailyDate)
  ) {
    const scenario = scenarioForDate(state.dailyDate);
    if (scenario && scenario.id === state.dailyScenarioId) {
      dailyResults.push({
        date: state.dailyDate,
        scenarioId: scenario.id,
        calendar: "london",
        won: state.winner === 0,
        margin: state.scores[0] - state.scores[1],
        hands: resultMetrics.hands,
        assisted: state.assisted,
        callMatched: dailyCallMatched(state, scenario),
      });
    }
  }
  return {
    ...current,
    hands: current.hands + 1,
    gameHands: current.gameHands + (completed ? resultMetrics.hands : 0),
    calls: current.calls + (state.lastHand.maker === 0 ? 1 : 0),
    callsMade: current.callsMade + (state.lastHand.maker === 0 && makerSucceeded ? 1 : 0),
    euchres: current.euchres + (state.lastHand.reason === "euchred" && makerTeam === 1 ? 1 : 0),
    aloneCalls: current.aloneCalls + (state.lastHand.maker === 0 && state.lastHand.alone ? 1 : 0),
    aloneMade: current.aloneMade + (state.lastHand.maker === 0 && state.lastHand.alone && makerSucceeded ? 1 : 0),
    games: current.games + (completed ? 1 : 0),
    wins: current.wins + (completed && state.winner === 0 ? 1 : 0),
    dailyResults: dailyResults.slice(-366),
    streak: dailyStreak(londonDailyDates(dailyResults)),
  };
}

export function dailyStreak(dates: string[]): number {
  const days = [...new Set(dates)]
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort()
    .map((date) => Date.parse(`${date}T00:00:00Z`))
    .filter(Number.isFinite);
  if (!days.length) return 0;
  let streak = 1;
  for (let index = days.length - 1; index > 0; index -= 1) {
    if (days[index] - days[index - 1] !== 86_400_000) break;
    streak += 1;
  }
  return streak;
}

export function claimScoreEvent(gameId: string, actionNonce: number): boolean {
  const eventId = `${gameId}:${actionNonce}`;
  if (localStorage.getItem(SCORE_EVENT_KEY) === eventId) return false;
  localStorage.setItem(SCORE_EVENT_KEY, eventId);
  return true;
}

export function clearAllLocalData(): void {
  localStorage.removeItem(PENDING_DAILY_KEY);
  localStorage.removeItem(SAVE_KEY);
  localStorage.removeItem(SETTINGS_KEY);
  localStorage.removeItem(STATS_KEY);
  localStorage.removeItem(SCORE_EVENT_KEY);
  localStorage.removeItem(VISITED_KEY);
}

const PENDING_DAILY_KEY = "trump-table:pending-daily:v1";
export function preservePendingDaily(): void {
  try {
    if (localStorage.getItem(PENDING_DAILY_KEY)) return;
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return;
    const state = JSON.parse(raw) as GameState;
    if (validateState(state) && state.mode === "daily" && state.dailyCalendar === "london" && state.dailyDate && state.dailyDate > (dailyReleaseThrough() ?? "")) localStorage.setItem(PENDING_DAILY_KEY, raw);
  } catch { /* Optional local storage. */ }
}
export function pendingDaily(): GameState | null {
  try {
    const raw = localStorage.getItem(PENDING_DAILY_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as GameState;
    return validateState(state) && isDailySessionRestorable(state, londonDateKey()) ? state : null;
  } catch { return null; }
}
